import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const AIChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hi! I\'m your DigiDreamSolutions assistant. How can I help you today?' }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const faqResponses = {
    pricing: 'We offer three plans: Free (forever), Pro Lite ($10/month), and Pro Max ($20/month). Visit our Pricing page to learn more!',
    features: 'DigiDreamSolutions includes 12+ apps: Digi Connect (networking), Digi Meet (video calls), Digi Schedule (meeting scheduler), CRM, Website Builder, Strategy Generator, and more!',
    support: 'You can reach our support team at support@digidreamsolutions.com or use this chat for quick questions!',
    free: 'Yes! We have a forever free plan that includes basic features like 2 websites, 10 CRM accounts, and limited generations per month.',
    trial: 'You can start with our free plan immediately - no credit card required! Upgrade anytime to unlock more features.',
    cancel: 'You can cancel your subscription anytime from your Subscription Dashboard. No questions asked!',
    apps: 'We offer 12 apps: Digi Connect, Digi Meet, Digi Schedule, Digi Community, Digi Strategy, Digi CRM, Website Builder, Events, Branding, Editor, Presentor, and DigiSlides Pro.',
    website: 'Our Website Builder lets you create professional websites with drag-and-drop. Free users can create 2 sites, Pro Lite gets 10 sites with custom domains!',
    crm: 'Digi CRM helps you manage leads, contacts, deals, and pipelines. Track your entire sales process in one place!',
    video: 'Digi Meet provides HD video conferencing with screen sharing, recording, and unlimited meeting time. Perfect for remote teams!'
  };

  const getAIResponse = (userMessage) => {
    const message = userMessage.toLowerCase();
    
    // Check for keywords
    if (message.includes('price') || message.includes('cost') || message.includes('pricing') || message.includes('plan')) {
      return faqResponses.pricing;
    }
    if (message.includes('feature') || message.includes('what can') || message.includes('what do')) {
      return faqResponses.features;
    }
    if (message.includes('support') || message.includes('help') || message.includes('contact')) {
      return faqResponses.support;
    }
    if (message.includes('free') || message.includes('trial')) {
      return faqResponses.free;
    }
    if (message.includes('cancel') || message.includes('refund')) {
      return faqResponses.cancel;
    }
    if (message.includes('app') || message.includes('tool')) {
      return faqResponses.apps;
    }
    if (message.includes('website') || message.includes('builder')) {
      return faqResponses.website;
    }
    if (message.includes('crm') || message.includes('customer')) {
      return faqResponses.crm;
    }
    if (message.includes('video') || message.includes('meet') || message.includes('call')) {
      return faqResponses.video;
    }
    if (message.includes('hi') || message.includes('hello') || message.includes('hey')) {
      return 'Hello! How can I assist you with DigiDreamSolutions today?';
    }
    if (message.includes('thank')) {
      return 'You\'re welcome! Feel free to ask if you have more questions.';
    }
    
    // Default response
    return 'I can help you with questions about our features, pricing, support, and more! Try asking about our plans, apps, or specific tools like CRM or Website Builder.';
  };

  const handleSend = () => {
    if (!input.trim()) return;

    const userMessage = { role: 'user', content: input };
    setMessages([...messages, userMessage]);
    setInput('');
    setIsTyping(true);

    // Simulate AI typing
    setTimeout(() => {
      const aiResponse = getAIResponse(input);
      setMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);
      setIsTyping(false);
    }, 1000);
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 rounded-full shadow-2xl hover:shadow-3xl transition-all hover:scale-110"
          aria-label="Open chat"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl flex flex-col" style={{ height: '600px', maxHeight: 'calc(100vh - 3rem)' }}>
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold">DigiDream Assistant</h3>
                <p className="text-xs text-blue-100">Always here to help</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-2xl ${
                    message.role === 'user'
                      ? 'bg-blue-600 text-white rounded-br-none'
                      : 'bg-white text-slate-800 rounded-bl-none shadow-sm'
                  }`}
                >
                  <p className="text-sm">{message.content}</p>
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white text-slate-800 p-3 rounded-2xl rounded-bl-none shadow-sm">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-slate-200 bg-white rounded-b-2xl">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Ask me anything..."
                className="flex-1"
              />
              <Button
                onClick={handleSend}
                className="bg-blue-600 hover:bg-blue-700"
                size="icon"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatbot;